// SPDX-License-Identifier: BUSL-1.1

/**
 * Live SSE stream panel for real-time session event monitoring.
 *
 * Renders a collapsible section showing connection status, running cost,
 * and the last 20 events with color-coded type badges.
 *
 */

import { useState } from 'react';
import {
  useSessionStream,
  type StreamEvent,
} from '@/hooks/use-session-stream';
import { Button } from '@/components/ui/button';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

/** Event type to badge color mapping. */
const EVENT_COLORS: Record<string, string> = {
  'agent.text': 'bg-blue-900/30 text-blue-400',
  'agent.text_complete': 'bg-blue-900/30 text-blue-300',
  'agent.tool_use': 'bg-purple-900/30 text-purple-400',
  'agent.tool_result': 'bg-purple-900/30 text-purple-300',
  'session.idle': 'bg-yellow-900/40 text-yellow-400',
  'session.error': 'bg-red-900/30 text-red-400',
  'session.terminated': 'bg-neutral-700 text-neutral-300',
  'span.model_request_end': 'bg-green-900/30 text-green-400',
};

function EventBadge({ type }: { type: string }) {
  const colors = EVENT_COLORS[type] ?? 'bg-neutral-700 text-neutral-300';
  return (
    <span
      className={`inline-flex shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium ${colors}`}
    >
      {type}
    </span>
  );
}

function EventRow({ event }: { event: StreamEvent }) {
  const time = new Date(event.timestamp).toLocaleTimeString();
  const preview =
    event.data.length > 120
      ? event.data.slice(0, 120) + '...'
      : event.data;

  return (
    <div className="flex items-start gap-2 border-b border-neutral-800 px-2 py-1.5 text-xs">
      <span className="shrink-0 text-neutral-500">{time}</span>
      <EventBadge type={event.event} />
      <span className="min-w-0 break-all font-mono text-neutral-400">
        {preview}
      </span>
    </div>
  );
}

/** Number of events shown in the visible log. */
const VISIBLE_EVENTS = 20;

export function LiveStreamPanel({
  sessionId,
}: {
  sessionId: string | null;
}) {
  const [expanded, setExpanded] = useState(true);
  const { connected, events, totalCost, error, disconnect } =
    useSessionStream(sessionId, API_BASE);

  if (!sessionId) return null;

  const visibleEvents = events.slice(-VISIBLE_EVENTS);

  return (
    <div className="mt-6 rounded-lg border border-neutral-700 bg-neutral-900">
      {/* Header */}
      <Button
        variant="ghost"
        onClick={() => setExpanded(!expanded)}
        className="flex h-auto w-full items-center justify-between rounded-none rounded-t-lg px-4 py-3 hover:bg-neutral-800/50"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-neutral-200">
            Live Stream
          </h3>
          {/* Connection indicator */}
          <span
            className={`inline-block h-2 w-2 rounded-full ${
              connected ? 'bg-green-500' : 'bg-neutral-600'
            }`}
            title={connected ? 'Connected' : 'Disconnected'}
          />
          {connected && (
            <span className="text-xs text-neutral-400">
              connected
            </span>
          )}
          {error && (
            <span className="text-xs text-red-400">{error}</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          {totalCost > 0 && (
            <span className="text-xs font-mono text-yellow-400">
              ${totalCost.toFixed(4)}
            </span>
          )}
          <span className="text-xs text-neutral-500">
            {events.length} event{events.length !== 1 ? 's' : ''}
          </span>
          <span className="text-neutral-500">
            {expanded ? '\u25B2' : '\u25BC'}
          </span>
        </div>
      </Button>

      {/* Body */}
      {expanded && (
        <div className="border-t border-neutral-800">
          {/* Controls */}
          <div className="flex items-center gap-2 px-4 py-2">
            <span className="text-xs font-mono text-neutral-500">
              {sessionId.slice(0, 12)}...
            </span>
            {connected && (
              <Button
                variant="outline"
                size="sm"
                onClick={disconnect}
              >
                Disconnect
              </Button>
            )}
          </div>

          {/* Event log */}
          <div className="max-h-80 overflow-y-auto">
            {visibleEvents.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-neutral-500">
                {connected
                  ? 'Waiting for events...'
                  : 'Not connected'}
              </p>
            ) : (
              visibleEvents.map((ev, i) => (
                <EventRow key={`${ev.timestamp}-${i}`} event={ev} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
